import { useEffect } from "react";
import {
  createBrowserRouter,
  Outlet,
  RouterProvider,
  useLocation,
} from "react-router-dom";
import { AppShell } from "@/components/patterns/app-shell";
import { ProtectedRoute } from "@/app/protected-route";
import { AuthLayout } from "@/routes/auth-layout";
import { ErrorPage } from "@/routes/error-page";
import { Skeleton } from "@/components/ui/skeleton";
import { m } from "@/i18n/en";

function RouterFallback() {
  return (
    <main
      aria-busy="true"
      aria-label={m.loading}
      className="mx-auto grid min-h-screen max-w-5xl content-center gap-4 px-6"
    >
      <Skeleton className="h-12 w-64" />
      <Skeleton className="h-72 w-full" />
    </main>
  );
}

function AccountLayout() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0 });
    requestAnimationFrame(() =>
      document.querySelector<HTMLElement>("#main-content h1")?.focus(),
    );
  }, [location.pathname]);
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

const router = createBrowserRouter([
  {
    element: <AuthLayout />,
    errorElement: <ErrorPage />,
    hydrateFallbackElement: <RouterFallback />,
    children: [
      {
        path: "/sign-in",
        lazy: async () => ({
          Component: (await import("@/routes/sign-in")).SignInPage,
        }),
      },
      {
        path: "/create-account",
        lazy: async () => ({
          Component: (await import("@/routes/create-account"))
            .CreateAccountPage,
        }),
      },
      {
        path: "/verify-email",
        lazy: async () => ({
          Component: (await import("@/routes/token-actions")).VerifyEmailPage,
        }),
      },
      {
        path: "/recover",
        lazy: async () => ({
          Component: (await import("@/routes/recover")).RecoverPage,
        }),
      },
      {
        path: "/reset-password",
        lazy: async () => ({
          Component: (await import("@/routes/token-actions")).ResetPasswordPage,
        }),
      },
    ],
  },
  {
    element: <ProtectedRoute />,
    errorElement: <ErrorPage />,
    hydrateFallbackElement: <RouterFallback />,
    children: [
      {
        element: <AccountLayout />,
        children: [
          {
            index: true,
            lazy: async () => ({
              Component: (await import("@/routes/overview")).OverviewPage,
            }),
          },
          {
            path: "personal-info",
            lazy: async () => ({
              Component: (await import("@/routes/personal-info"))
                .PersonalInfoPage,
            }),
          },
          {
            path: "security",
            lazy: async () => ({
              Component: (await import("@/routes/security")).SecurityPage,
            }),
          },
          {
            path: "sessions",
            lazy: async () => ({
              Component: (await import("@/routes/sessions")).SessionsPage,
            }),
          },
          {
            path: "products",
            lazy: async () => ({
              Component: (await import("@/routes/products")).ProductsPage,
            }),
          },
          {
            path: "*",
            lazy: async () => ({
              Component: (await import("@/routes/not-found")).NotFoundPage,
            }),
          },
        ],
      },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
